import { ChangeDetectionStrategy, Component, EventEmitter, OnInit, Output, ViewChild, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ChatWidgetComponent } from '../chat-widget/chat-widget.component';
import { DashboardService } from '../../core/services/dashboard.service';
import { ClaimOverviewComponent } from './claim-overview/claim-overview.component';
import { DashboardQuickActionRequest, QuickActionsComponent } from './quick-actions/quick-actions.component';
import { FaqComponent } from './faq/faq.component';
import { QuickActionResponse, QuickActionType } from '../../core/models/quick-action.model';
import { QuickActionWizardComponent } from '../../shared/components/quick-action-wizard/quick-action-wizard.component';
import { TranslatePipe } from '../../shared/pipes/translate.pipe';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [CommonModule, ChatWidgetComponent, ClaimOverviewComponent, QuickActionsComponent, FaqComponent, QuickActionWizardComponent, TranslatePipe],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardComponent implements OnInit {
  private readonly dashboardService = inject(DashboardService);

  @Output() readonly reloginRequired = new EventEmitter<string | undefined>();
  @ViewChild(ChatWidgetComponent) private chatWidget?: ChatWidgetComponent;

  readonly loading = this.dashboardService.loading;
  readonly error = this.dashboardService.error;
  readonly blocked = this.dashboardService.blocked;
  readonly caseHeader = this.dashboardService.caseHeader;
  readonly dashboard = this.dashboardService.viewModel;

  readonly chatExpanded = signal(true);
  readonly chatHeight = computed(() => (this.chatExpanded() ? 600 : 400));
  readonly activeQuickAction = signal<QuickActionType | null>(null);
  readonly activeQuickActionInitialStep = signal<string | null>(null);
  readonly activeQuickActionInitialPayload = signal<Record<string, unknown> | null>(null);
  readonly activeQuickActionOrigin = signal<'quick_action' | 'chat'>('quick_action');
  readonly activeContactCallbackExpanded = signal(false);

  ngOnInit(): void {
    this.dashboardService.load((message) => this.requireReLogin(message));
  }

  runQuickAction(request: DashboardQuickActionRequest): void {
    this.activeQuickActionInitialStep.set(null);
    this.activeQuickActionInitialPayload.set(null);
    this.activeQuickActionOrigin.set('quick_action');
    this.activeContactCallbackExpanded.set(request.contactCallbackExpanded);
    this.activeQuickAction.set(request.actionType);
  }

  runChatQuickAction(request: { actionType: QuickActionType; step?: string; payload?: Record<string, unknown> }): void {
    this.activeQuickActionInitialStep.set(request.step ?? null);
    this.activeQuickActionInitialPayload.set(request.payload ?? null);
    this.activeQuickActionOrigin.set('chat');
    this.activeContactCallbackExpanded.set(false);
    this.activeQuickAction.set(request.actionType);
  }

  closeQuickAction(): void {
    this.chatWidget?.cancelPendingPaymentContract();
    this.activeQuickAction.set(null);
    this.activeQuickActionInitialStep.set(null);
    this.activeQuickActionInitialPayload.set(null);
    this.activeQuickActionOrigin.set('quick_action');
    this.activeContactCallbackExpanded.set(false);
  }

  onQuickActionCompleted(response: QuickActionResponse): void {
    // The chat modal bridge persists and shows the backend confirmation itself.
    if (this.chatWidget?.completePendingPaymentContract(response)) {
      return;
    }

    this.chatWidget?.addQuickActionConfirmation(response.completion_message ?? '', response.debugExport);
  }

  requireReLogin(message?: string): void {
    this.reloginRequired.emit(message);
  }
}
